/* eslint-disable react/prop-types */
import React from 'react';

import { AnchorLayout, LazyMount } from '../../ui';
import { MEMBER_ITEMS, sidOf } from './memberOps/constants';
import { useMemberOps } from './memberOps/useMemberOps';
import OverviewPane from './memberOps/OverviewPane';
import PortraitPane from './memberOps/PortraitPane';
import BehaviorPane from './memberOps/BehaviorPane';
import ClockInPane from './memberOps/ClockInPane';
import ActivityPane from './memberOps/ActivityPane';
import styles from './board.less';

/**
 * 会员经营（二级 tab）· 容器
 * ==================================================================
 * 本文件只负责三件事：预取、拼装、锚点导航。五个区块的展示实现在
 * `./memberOps/*Pane.jsx`，取数在 `./memberOps/useMemberOps.js`，
 * 区块列表与锚点 id 在 `./memberOps/constants.js`（2026-09-17 拆分，方案 P1-1）。
 *
 * 2026-09-04 第五次反馈：会员概览 / 会员画像 / 看得见活动 / 会员行为 /
 * 会员打卡 这五块**本身是 tab**，不该在一屏里全部堆完。
 * 同时按参考稿修正三处：
 *   · 会员转化漏斗：横向漏斗条 + **相对总会员数**的圆环百分比 + 建议卡
 *     （此前是"灰底进度条 + 相对上一级百分比"，语义和形态都不对）
 *   · 会员活跃 TOP：文字列表改成排名条，前三名奖杯徽标，并独占一行拿宽度
 *   · 最近 7 天完课情况：三个标签改成环形分布图（框架文档写的就是环形图）
 *
 * 2026-09-17 结构调整（方案 P1：三层 → 两层）：
 * 这五块原先做成三级 tab（SubNav 右侧下拉），切一下才看得到一块；
 * 而它们本来就是「同一个实体的多段信息」——换成 tab 会让用户丢掉上下文。
 * 现在改为**单页平铺 + 右侧锚点导航**（AnchorLayout），一屏内可滚动对照，
 * 锚点与滚动联动、点击写 hash 可分享。
 *
 * 取舍与对策：单页模型下五块默认会同时挂载（请求"一次全发"+ 白渲染）。
 * 对策是每块外面包 LazyMount：滚动到视口附近才真正挂载，
 * 未挂载时用等高占位保住 section 高度（否则锚点位置会算错）。
 */
export { MEMBER_ITEMS } from './memberOps/constants';

const MemberOps = (props) => {
  /* encryptTenantId 由 BoardTab 传入，本容器自己不用，但要转交给 ActivityPane
     （它要拼 H5 深链 h5Src）。漏解构会直接 ReferenceError 白屏——已踩过。 */
  const { tenantId, coachUserId, enums = {}, encryptTenantId } = props;
  const { overview, portrait, visit, status, reload } = useMemberOps({
    tenantId,
    coachUserId,
  });

  const panes = {
    overview: (
      <OverviewPane
        data={overview}
        enums={enums}
        loading={status.overview.loading}
        error={status.overview.error}
        onRetry={reload}
      />
    ),
    portrait: (
      <PortraitPane
        data={portrait}
        tenantId={tenantId}
        coachUserId={coachUserId}
        loading={status.portrait.loading}
        error={status.portrait.error}
        onRetry={reload}
      />
    ),
    behavior: (
      <BehaviorPane
        visit={visit}
        loading={status.visit.loading}
        error={status.visit.error}
        onRetry={reload}
      />
    ),
    /* 打卡与看得见活动各自取数：要按自身筛选/分页重新请求，
       放在各自 pane 里才能配合 LazyMount（滚到才发请求） */
    clockin: (
      <ClockInPane
        tenantId={tenantId}
        coachUserId={coachUserId}
        enums={enums}
      />
    ),
    activity: (
      <ActivityPane
        tenantId={tenantId}
        coachUserId={coachUserId}
        encryptTenantId={encryptTenantId}
      />
    ),
  };

  /* section 的 id 由 MEMBER_ITEMS 统一生成（列表与 DOM id 同一个来源，
     避免两处手写导致锚点对不上） */
  return (
    <AnchorLayout
      items={MEMBER_ITEMS.map((it) => ({
        key: sidOf(it.key),
        title: it.label,
      }))}
    >
      {MEMBER_ITEMS.map((it) => (
        <section
          key={it.key}
          id={sidOf(it.key)}
          className={`${styles.anchorSection} ${
            it.key === 'activity' ? styles.activitySection : ''
          }`}
        >
          <LazyMount>{panes[it.key]}</LazyMount>
        </section>
      ))}
    </AnchorLayout>
  );
};

export default MemberOps;
